import {Component, Inject} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {UsersService} from "../../../services/users.service";
import {IInviteGameAdminUserRequest} from "../../../interfaces/IInviteGameAdminUserRequest";
import {SnackbarService} from "../../../services/snackbar.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {AdminInviteType} from "../../../enum/AdminInviteType";
import {Observable} from "rxjs";
import {ValidateAdminEmailInviteResult} from "../../../interfaces/ICheckIfUserExistsResult";
import {InvalidEmailEntryReasonType} from "../../../enum/InvalidEmailEntryReasonType";
import {GameAdminInviteUIEnum} from "../../../enum/GameAdminInviteUIEnum";
import {IGenericAdminUserRequest} from "../../../interfaces/IGenericAdminUserRequest";
import {IAdminQueryResult} from "../../../interfaces/IAdminQueryResult";
import {IAddNewRoleCommand} from "../../../interfaces/IAddNewRoleCommand";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {IRole} from "../../../interfaces/IRole";
import {PermissionsService} from "../../../services/permissions.service";
import {IUpgradeAdminToCausableAdmin} from "../../../interfaces/IUpgradeAdminToCausableAdmin";
import {DialogBaseComponent} from "../dialog-base/dialog-base.component";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatTooltip} from "@angular/material/tooltip";
import {MatButton} from "@angular/material/button";
import {PipesModule} from "../../../pipes/pipes.module";
import {DatePipe} from "@angular/common";
import {ICharity} from "../../../interfaces/charity/ICharity";
import {CharityService} from "../../../services/charity.service";
import {IInviteCharityAdminUserRequest} from "../../../interfaces/IInviteCharityAdminUserRequest";
import {ISendCrossGameAdminVerificationEmail} from "../../../interfaces/ISendCrossGameAdminVerificationEmail";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {IUpgradeAdminToCrossGame} from "../../../interfaces/IUpgradeAdminToCrossGame";

@Component({
  selector: 'app-add-new-admin',
  standalone: true,
  templateUrl: './add-new-admin.component.html',
  imports: [
    DialogBaseComponent,
    MatLabel,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    MatTooltip,
    MatButton,
    FormsModule,
    PipesModule,
    DatePipe
  ],
  styleUrls: ['./add-new-admin.component.css']
})
export class AddNewAdminComponent {

  public uiState: GameAdminInviteUIEnum = GameAdminInviteUIEnum.ValidateEmail;
  public uiStateEnumForTemplate = GameAdminInviteUIEnum;
  public newUserVerificationCode: string = '';
  public invalidEmailReason: InvalidEmailEntryReasonType | undefined;
  public existingAdmin: IAdminQueryResult | undefined;
  public possibleRoles: IRole[] = [];
  public charities: ICharity[] = [];
  public adminInviteTypeForTemplate = AdminInviteType;
  public newUserEmail: string = '';
  public addAdminForm = new FormGroup({
    firstName: new FormControl<string>('', Validators.required),
    lastName: new FormControl<string>('', Validators.required),
    newRoles: new FormControl<string[]>([]),
    charity: new FormControl<string>('')
  });

  public updateExistingAdminForm = new FormGroup({
    newRolesForExistingAdmin: new FormControl<string[]>([])
  });

  constructor(private usersService: UsersService,
              private permissionService: PermissionsService,
              private charityService: CharityService,
              private matDialogRef: MatDialogRef<AddNewAdminComponent>,
              private errorHandlingService: ErrorHandlingService,
              private snackBarService: SnackbarService,
              @Inject(MAT_DIALOG_DATA) public addNewAdminInviteData: {
                inviteType: AdminInviteType;
                game: IGameQueryResult;
              }) {
    if (addNewAdminInviteData.inviteType == AdminInviteType.GameAdminInvite || addNewAdminInviteData.inviteType == AdminInviteType.CrossGameInvite) {
      this.permissionService.fetchPossibleRoles(addNewAdminInviteData.game.Id).subscribe({
        next: (rolesP) => {
          if (addNewAdminInviteData.inviteType == AdminInviteType.CrossGameInvite) {
            rolesP = rolesP.filter(x => x.IsCrossGameRole);
          } else {
            rolesP = rolesP.filter(x => !x.IsCrossGameRole);
          }

          this.possibleRoles = rolesP;
        },
        error: (error) => {
          this.errorHandlingService.displayDialogLevelErrorMessage(error);
        }
      })
    }

    if (addNewAdminInviteData.inviteType == AdminInviteType.CharityAdminInvite) {
      this.charityService.fetchActiveCharitiesForGame(addNewAdminInviteData.game.Id).subscribe({
        next: (activeCharitiesResultP) => {
          this.charities = activeCharitiesResultP;
        },
        error: (error) => {
          this.errorHandlingService.displayDialogLevelErrorMessage(error);
        }
      });
    }
  }

  validateEmail() {
    this.uiState = GameAdminInviteUIEnum.ShowLoading;
    this.invalidEmailReason = undefined;

    let validateEmailApiCall: Observable<ValidateAdminEmailInviteResult>;

    switch (this.addNewAdminInviteData.inviteType) {
      case AdminInviteType.GameAdminInvite:
      case AdminInviteType.CharityAdminInvite: {
        validateEmailApiCall = this.usersService.validateEmailForGameAdminInvite(this.newUserEmail, this.addNewAdminInviteData.game.Id);
        break;
      }
      case AdminInviteType.CausableInvite: {
        validateEmailApiCall = this.usersService.validateEmailForCausableAdminInvite(this.newUserEmail);
        break;
      }
      case AdminInviteType.CrossGameInvite: {
        validateEmailApiCall = this.usersService.validateEmailForCrossGameAdminInvite(this.newUserEmail);
        break;
      }

      default: {
        return;
      }
    }

    validateEmailApiCall!.subscribe({
      next: (res) => {
        if (res.CanAddUserAsAdmin) {
          // email passes check so the existing admin can be added to this game/org
          if (res.ExistingAdminData) {
            this.existingAdmin = res.ExistingAdminData;
            this.uiState = GameAdminInviteUIEnum.AddExistingUser;
          } else {
            // admin does not exist yet so the email can be added to admin app
            this.uiState = GameAdminInviteUIEnum.InputUserData;
          }
        } else {
          // user email cannot be used
          this.invalidEmailReason = res?.ReasoningMessageType;
          this.uiState = GameAdminInviteUIEnum.ValidateEmail;
        }
      },
      error: (error) => {
        this.uiState = GameAdminInviteUIEnum.ValidateEmail;
        this.errorHandlingService.displayDialogLevelErrorMessage(error, true);
      }
    })

  }

  sendInvite() {
    switch (this.addNewAdminInviteData.inviteType) {
      case AdminInviteType.GameAdminInvite:
        this.sendGameAdminInvite();
        break;
      case AdminInviteType.CausableInvite:
        this.sendCausableAdminInvite();
        break;
      case AdminInviteType.CharityAdminInvite:
        this.addCharityAdmin();
        break;
      case AdminInviteType.CrossGameInvite:
        this.addCrossGameAdmin();
        break;
    }
  }

  addExistingUser() {
    switch (this.addNewAdminInviteData.inviteType) {
      case AdminInviteType.GameAdminInvite:
        this.updateExistingGameAdmin();
        break;
      case AdminInviteType.CausableInvite:
        this.upgradeToCausableAdmin();
        break;
      case AdminInviteType.CharityAdminInvite:
        this.addCharityAdmin();
        break;
      case AdminInviteType.CrossGameInvite:
        this.upgradeToCrossGameAdmin();
        break;
    }
  }

  private upgradeToCrossGameAdmin() {
    const chosenRoles = this.updateExistingAdminForm.controls.newRolesForExistingAdmin.value;

    if (this.updateExistingAdminForm.valid && this.existingAdmin && (chosenRoles && chosenRoles.length > 0)) {
      this.uiState = GameAdminInviteUIEnum.ShowLoading;

      const upgradeRequest: IUpgradeAdminToCrossGame = {
        targetAdminId: this.existingAdmin.Id,
        adminEmail: this.existingAdmin.Email,
        adminFirstName: this.existingAdmin.FirstName,
        adminLastName: this.existingAdmin.LastName,
        redirectUrl: `${window.location.origin}/login`,
        roles: chosenRoles!
      }

      this.permissionService.upgradeToCrossGameAdmin(upgradeRequest).subscribe({
        next: () => {
          this.snackBarService.openSuccessfulSnackBar('Successfully Upgraded to Causable Admin');
          this.matDialogRef.close(true);
        },
        error: (error) => {
          this.uiState = GameAdminInviteUIEnum.ValidateEmail;
          this.errorHandlingService.displayDialogLevelErrorMessage(error, true);
        }
      })
    }
  }

  private upgradeToCausableAdmin() {
    if (this.updateExistingAdminForm.valid && this.existingAdmin) {
      this.uiState = GameAdminInviteUIEnum.ShowLoading;

      const upgradeRequest: IUpgradeAdminToCausableAdmin = {
        targetAdminId: this.existingAdmin.Id,
        adminEmail: this.existingAdmin.Email,
        adminFirstName: this.existingAdmin.FirstName,
        adminLastName: this.existingAdmin.LastName,
        gameName: this.addNewAdminInviteData.game.Name,
        redirectUrl: `${window.location.origin}/login`
      }

      this.permissionService.upgradeToCausableAdmin(upgradeRequest).subscribe({
        next: () => {
          this.snackBarService.openSuccessfulSnackBar('Successfully Upgraded to Causable Admin');
          this.matDialogRef.close(true);
        },
        error: (error) => {
          this.uiState = GameAdminInviteUIEnum.ValidateEmail;
          this.errorHandlingService.displayDialogLevelErrorMessage(error, true);
        }
      })
    }
  }

  private updateExistingGameAdmin() {
    const chosenRoles = this.updateExistingAdminForm.controls.newRolesForExistingAdmin.value;

    if (this.updateExistingAdminForm.valid && this.existingAdmin && (chosenRoles && chosenRoles.length > 0)) {
      this.uiState = GameAdminInviteUIEnum.ShowLoading;

      const gameAdminRequest: IAddNewRoleCommand = {
        targetAdminId: this.existingAdmin.Id,
        gameId: this.addNewAdminInviteData.game.Id,
        roleIds: this.updateExistingAdminForm.controls.newRolesForExistingAdmin.value!,
        adminEmail: this.existingAdmin.Email,
        adminFirstName: this.existingAdmin.FirstName,
        adminLastName: this.existingAdmin.LastName,
        gameName: this.addNewAdminInviteData.game.Name,
        redirectUrl: `${window.location.origin}/login`
      }

      this.permissionService.addNewUserRole(gameAdminRequest).subscribe({
        next: () => {
          this.snackBarService.openSuccessfulSnackBar('Successfully Added New Game Admin');
          this.matDialogRef.close(true);
        },
        error: (error) => {
          this.uiState = GameAdminInviteUIEnum.ValidateEmail;
          this.errorHandlingService.displayDialogLevelErrorMessage(error, true);
        }
      })
    }
  }

  private sendCausableAdminInvite() {
    if (this.addAdminForm.valid) {
      this.uiState = GameAdminInviteUIEnum.ShowLoading;
      const inviteRequest: IGenericAdminUserRequest = {
        email: this.newUserEmail,
        lastName: this.addAdminForm.controls.lastName.value!,
        firstName: this.addAdminForm.controls.firstName.value!,
        redirectUrl: `${window.location.origin}/login`,
        adminInviteType: this.addNewAdminInviteData.inviteType
      }

      this.usersService.sendCausableAdminUserInvite(inviteRequest).subscribe({
        next: (res) => {
          this.snackBarService.openSuccessfulSnackBar('Invite Sent Successfully')
          this.newUserVerificationCode = res.AdminVerificationCode;
          this.uiState = GameAdminInviteUIEnum.ViewVerificationCode;
          this.usersService.getUpdatedAdminUsersBehaviorSubject.next(true);
        },
        error: (error) => {
          this.uiState = GameAdminInviteUIEnum.ValidateEmail;
          this.errorHandlingService.displayDialogLevelErrorMessage(error, true);
        }
      })
    }
  }

  private sendGameAdminInvite() {
    if (this.addAdminForm.valid) {
      this.uiState = GameAdminInviteUIEnum.ShowLoading;
      const inviteRequest: IInviteGameAdminUserRequest = {
        email: this.newUserEmail,
        lastName: this.addAdminForm.controls.lastName.value!,
        firstName: this.addAdminForm.controls.firstName.value!,
        gameId: this.addNewAdminInviteData.game.Id,
        gameName: this.addNewAdminInviteData.game.Name,
        redirectUrl: `${window.location.origin}/login`,
        gameAdminRoleIds: this.addAdminForm.controls.newRoles.value!
      }

      this.usersService.sendNewGameAdminUserInvite(inviteRequest).subscribe({
        next: (res) => {
          this.snackBarService.openSuccessfulSnackBar('Invite Sent Successfully')
          this.newUserVerificationCode = res.AdminVerificationCode;
          this.uiState = GameAdminInviteUIEnum.ViewVerificationCode;
          this.usersService.getUpdatedAdminUsersBehaviorSubject.next(true);
        },
        error: (error) => {
          this.uiState = GameAdminInviteUIEnum.ValidateEmail;
          this.errorHandlingService.displayDialogLevelErrorMessage(error, true);
        }
      })
    }
  }

  private addCharityAdmin() {
    if (this.addAdminForm.valid) {
      this.uiState = GameAdminInviteUIEnum.ShowLoading;
      const inviteRequest: IInviteCharityAdminUserRequest = {
        email: this.newUserEmail,
        lastName: this.addAdminForm.controls.lastName.value!,
        firstName: this.addAdminForm.controls.firstName.value!,
        gameId: this.addNewAdminInviteData.game.Id,
        gameName: this.addNewAdminInviteData.game.Name,
        charityId: this.addAdminForm.controls.charity.value!,
        redirectUrl: `${window.location.origin}/login`
      }

      this.usersService.sendNewCharityAdminUserInvite(inviteRequest).subscribe({
        next: (res) => {
          this.snackBarService.openSuccessfulSnackBar('Invite Sent Successfully')
          this.newUserVerificationCode = res.AdminVerificationCode;
          this.uiState = GameAdminInviteUIEnum.ViewVerificationCode;
          this.usersService.getUpdatedAdminUsersBehaviorSubject.next(true);
        },
        error: (error) => {
          this.uiState = GameAdminInviteUIEnum.ValidateEmail;
          this.errorHandlingService.displayDialogLevelErrorMessage(error, true);
        }
      })
    }
  }

  private addCrossGameAdmin() {
    if (this.addAdminForm.valid) {
      this.uiState = GameAdminInviteUIEnum.ShowLoading;

      const inviteRequest: ISendCrossGameAdminVerificationEmail = {
        email: this.newUserEmail,
        lastName: this.addAdminForm.controls.lastName.value!,
        firstName: this.addAdminForm.controls.firstName.value!,
        redirectUrl: `${window.location.origin}/login`,
        crossGameRoleIds: this.addAdminForm.controls.newRoles.value!
      }

      this.usersService.sendNewCrossGameAdminUserInvite(inviteRequest).subscribe({
        next: (res) => {
          this.snackBarService.openSuccessfulSnackBar('Invite Sent Successfully')
          this.newUserVerificationCode = res.AdminVerificationCode;
          this.uiState = GameAdminInviteUIEnum.ViewVerificationCode;
          this.usersService.getUpdatedAdminUsersBehaviorSubject.next(true);
        },
        error: (error) => {
          this.uiState = GameAdminInviteUIEnum.ValidateEmail;
          this.errorHandlingService.displayDialogLevelErrorMessage(error, true);
        }
      })
    }
  }
}
